import { useState } from "react";
import { AlertOctagon, LoaderCircle, RadioTower } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../hooks/use-auth";
import { useDashboard } from "../hooks/use-dashboard";
import { api, getErrorMessage } from "../lib/api";
import { Button, type ButtonProps } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";

export function LiveActivationDialog({
  open,
  onOpenChange,
  onConfirmed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmed: () => Promise<void>;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function changeOpen(nextOpen: boolean) {
    if (!nextOpen) {
      setAcknowledged(false);
      setError(null);
    }
    onOpenChange(nextOpen);
  }

  async function confirm() {
    setError(null);
    setBusy(true);
    try {
      await onConfirmed();
      changeOpen(false);
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent>
        <DialogHeader>
          <span className="mb-1 grid size-10 place-items-center rounded-full bg-accent text-accent-foreground">
            <RadioTower className="size-4" />
          </span>
          <DialogTitle>Start live trading?</DialogTitle>
          <DialogDescription>
            Hyper Profits can place, edit, and cancel ETH/USD spot orders without asking for approval each time. There is no artificial daily trade limit. Available balance, Kraken restrictions, valid strategy setups, and enabled safety controls still apply.
          </DialogDescription>
        </DialogHeader>
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-background/65 p-4 text-sm leading-6">
          <input
            type="checkbox"
            className="mt-1 size-4 accent-[var(--primary)]"
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
          />
          <span>I understand that live trading uses real funds and the full amount committed to a position can be lost.</span>
        </label>
        {error && <p className="rounded-xl bg-coral-soft px-4 py-3 text-sm text-destructive" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => changeOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="signal" onClick={() => void confirm()} disabled={!acknowledged || busy}>
            {busy && <LoaderCircle data-icon="inline-start" className="animate-spin" />}
            Start live trading
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EmergencyControl({
  size = "default",
  variant = "outline",
  className,
  compact = false,
  responsiveLabel = false,
}: Pick<ButtonProps, "size" | "variant" | "className"> & { compact?: boolean; responsiveLabel?: boolean }) {
  const { session } = useAuth();
  const { dashboard, replaceDashboard } = useDashboard();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const unavailable = !dashboard || (dashboard.bot.status === "stopped" && !dashboard.position);

  async function confirmEmergency() {
    if (!session.csrfToken) {
      toast.error("Your secure session needs to be refreshed.");
      return;
    }
    setBusy(true);
    try {
      const snapshot = await api.emergency(session.csrfToken);
      replaceDashboard(snapshot);
      toast.success(snapshot.position
        ? "Close requested. New entries are stopped while Kraken confirms the exit."
        : "Position closed and bot stopped.");
      setOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size={size}
          variant={variant}
          className={className}
          disabled={unavailable}
          aria-label={compact ? "Close position and stop bot" : undefined}
          title={unavailable ? "No running bot or open position" : compact ? "Close position and stop bot" : undefined}
        >
          <AlertOctagon data-icon="inline-start" />
          {!compact && <span className={responsiveLabel ? "sr-only lg:not-sr-only" : undefined}>Close position & stop bot</span>}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <span className="mb-1 grid size-10 place-items-center rounded-full bg-muted text-foreground">
            <AlertOctagon className="size-4" />
          </span>
          <AlertDialogTitle>Close the position and stop the bot?</AlertDialogTitle>
          <AlertDialogDescription>
            Pending orders will be cancelled and a market order may be used to close held ETH. The final execution price and fees can differ from the current estimate.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <p className="rounded-xl bg-muted px-4 py-3 text-xs font-semibold leading-5 text-muted-foreground">
          This action cannot be undone from the dashboard.
        </p>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button variant="ghost" disabled={busy}>Keep running</Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={(event) => {
                event.preventDefault();
                void confirmEmergency();
              }}
            >
              {busy && <LoaderCircle data-icon="inline-start" className="animate-spin" />}
              Close position & stop bot
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
